import { defineRecipe } from '@chakra-ui/react'

/**
 * Single-part recipes. Control geometry lives here so every button, input and
 * select shares one height scale, one radius and one focus treatment.
 *
 * Orange drives interaction: the focus ring, the primary fill and the hover tint
 * on neutral controls. Coral is destructive, blue informational. Nothing else
 * uses a brand colour.
 */

const CONTROL_SIZES = {
  xs: { height: 'control.xs', fontSize: '2xs', paddingInline: '2', gap: '1' },
  sm: { height: 'control.sm', fontSize: 'sm', paddingInline: '2.5', gap: '1.5' },
  md: { height: 'control.md', fontSize: 'md', paddingInline: '3.5', gap: '2' },
  lg: { height: 'control.lg', fontSize: 'md', paddingInline: '4', gap: '2' },
}

const FOCUS_RING = {
  outline: '2px solid',
  outlineColor: 'focus.ring',
  outlineOffset: '1px',
}

/** Disabled states name their colours instead of dimming with opacity. */
const DISABLED = {
  bg: 'bg.disabled',
  color: 'fg.disabled',
  borderColor: 'border.subtle',
  cursor: 'not-allowed',
  boxShadow: 'none',
}

const button = defineRecipe({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontFamily: 'body',
    fontWeight: 'medium',
    letterSpacing: 'tight',
    lineHeight: 'none',
    borderRadius: 'md',
    borderWidth: '1px',
    borderColor: 'transparent',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    transitionProperty: 'background-color, border-color, color, box-shadow',
    transitionDuration: 'fast',
    transitionTimingFunction: 'standard',
    _focusVisible: FOCUS_RING,
    _disabled: DISABLED,
    // One icon size for every button, set once.
    '& svg': { flexShrink: 0, width: 'icon.sm', height: 'icon.sm' },
  },

  variants: {
    variant: {
      /**
       * The single primary action on a page. Amber with espresso text — the same
       * pairing as the client site's hiring bar, and 7.6:1 in both modes.
       */
      solid: {
        bg: 'brand.solid',
        color: 'brand.contrast',
        _hover: { bg: 'brand.hover' },
        _active: { bg: 'brand.active' },
      },
      /** Default action beside a primary one. Warm hover, not grey. */
      secondary: {
        bg: 'bg.surfaceRaised',
        color: 'fg.default',
        borderColor: 'border.default',
        boxShadow: 'xs',
        _hover: { bg: 'bg.selected', borderColor: 'border.interactive' },
        _active: { bg: 'brand.muted', borderColor: 'brand.border' },
      },
      outline: {
        bg: 'transparent',
        color: 'fg.default',
        borderColor: 'border.default',
        _hover: { bg: 'bg.hover' },
        _active: { bg: 'bg.active' },
      },
      ghost: {
        bg: 'transparent',
        color: 'fg.muted',
        _hover: { bg: 'bg.hover', color: 'fg.default' },
        _active: { bg: 'bg.active' },
      },
      subtle: {
        bg: 'bg.surfaceSubtle',
        color: 'fg.default',
        _hover: { bg: 'bg.hover' },
        _active: { bg: 'bg.active' },
      },
      /** Coral, never orange. */
      destructive: {
        bg: 'status.error',
        color: 'fg.onSolid',
        _hover: { bg: 'status.errorFg' },
        _active: { bg: 'status.errorFg' },
      },
      /** Blue, for the rare distinctly informational action. */
      informational: {
        bg: 'accent.blueSubtle',
        color: 'status.infoFg',
        borderColor: 'accent.blueBorder',
        _hover: { bg: 'accent.blueBorder' },
      },
      /** Inline text action. No padding, no height. */
      link: {
        bg: 'transparent',
        color: 'brand.fg',
        height: 'auto',
        paddingInline: '0',
        _hover: { textDecoration: 'underline' },
        _disabled: { color: 'fg.disabled', bg: 'transparent', cursor: 'not-allowed' },
      },
      plain: {
        bg: 'transparent',
        color: 'fg.muted',
        height: 'auto',
        paddingInline: '0',
        _hover: { color: 'fg.default' },
      },
    },

    size: CONTROL_SIZES,
  },

  defaultVariants: { variant: 'secondary', size: 'md' },
})

const input = defineRecipe({
  base: {
    width: '100%',
    fontFamily: 'body',
    bg: 'bg.input',
    color: 'fg.default',
    borderWidth: '1px',
    borderColor: 'border.default',
    borderRadius: 'md',
    transitionProperty: 'border-color, box-shadow, background-color',
    transitionDuration: 'fast',
    _placeholder: { color: 'fg.subtle' },
    _hover: { borderColor: 'border.interactive' },
    _focusVisible: { borderColor: 'border.focus', ...FOCUS_RING, outlineOffset: '0px' },
    _disabled: DISABLED,
    // Errors are coral; orange is never an error colour.
    _invalid: {
      borderColor: 'status.errorBorder',
      _focusVisible: { outlineColor: 'status.error', borderColor: 'status.error' },
    },
  },
  variants: {
    size: {
      sm: { height: 'control.sm', fontSize: 'sm', paddingInline: '2.5' },
      md: { height: 'control.md', fontSize: 'md', paddingInline: '3' },
      lg: { height: 'control.lg', fontSize: 'md', paddingInline: '3' },
    },
  },
  defaultVariants: { size: 'md' },
})

const textarea = defineRecipe({
  base: {
    width: '100%',
    fontFamily: 'body',
    bg: 'bg.input',
    color: 'fg.default',
    borderWidth: '1px',
    borderColor: 'border.default',
    borderRadius: 'md',
    paddingInline: '3',
    paddingBlock: '2',
    fontSize: 'md',
    lineHeight: 'normal',
    _placeholder: { color: 'fg.subtle' },
    _hover: { borderColor: 'border.interactive' },
    _focusVisible: { borderColor: 'border.focus', ...FOCUS_RING, outlineOffset: '0px' },
    _disabled: DISABLED,
    _invalid: { borderColor: 'status.errorBorder' },
  },
})

/**
 * Status pills. Soft background with accessible text, never a saturated fill.
 * Tones map onto the logo colours: teal succeeds, orange waits, coral fails,
 * blue informs, warm grey archives.
 */
const statusBadge = defineRecipe({
  className: 'status-badge',
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '1.5',
    borderWidth: '1px',
    borderRadius: 'full',
    paddingInline: '2',
    paddingBlock: '0.5',
    fontSize: '2xs',
    fontWeight: 'medium',
    lineHeight: 'snug',
    whiteSpace: 'nowrap',
  },
  variants: {
    tone: {
      neutral: {
        bg: 'status.neutralSubtle',
        color: 'status.neutralFg',
        borderColor: 'status.neutralBorder',
      },
      success: {
        bg: 'status.successSubtle',
        color: 'status.successFg',
        borderColor: 'status.successBorder',
      },
      warning: {
        bg: 'status.warningSubtle',
        color: 'status.warningFg',
        borderColor: 'status.warningBorder',
      },
      error: {
        bg: 'status.errorSubtle',
        color: 'status.errorFg',
        borderColor: 'status.errorBorder',
      },
      info: {
        bg: 'status.infoSubtle',
        color: 'status.infoFg',
        borderColor: 'status.infoBorder',
      },
      brand: {
        bg: 'brand.subtle',
        color: 'brand.fg',
        borderColor: 'brand.border',
      },
    },
    shape: {
      pill: { borderRadius: 'full' },
      /** Squarer chip, for category tags rather than lifecycle status. */
      square: { borderRadius: 'sm', paddingInline: '1.5' },
    },
  },
  defaultVariants: { tone: 'neutral', shape: 'pill' },
})

/**
 * Sidebar and drawer navigation entries. Active state is a soft amber surface,
 * an amber icon and a thin left indicator — no gradient, no glow, no full-width
 * bright fill.
 */
const navItem = defineRecipe({
  className: 'nav-item',
  base: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '2.5',
    width: '100%',
    minHeight: 'nav',
    paddingInline: '2.5',
    borderRadius: 'md',
    fontSize: 'md',
    fontWeight: 'medium',
    letterSpacing: 'tight',
    color: 'fg.muted',
    textDecoration: 'none',
    transitionProperty: 'background-color, color',
    transitionDuration: 'fast',
    transitionTimingFunction: 'standard',
    _hover: { bg: 'bg.hover', color: 'fg.default' },
    _focusVisible: FOCUS_RING,
    '& svg': { flexShrink: 0, width: 'icon.md', height: 'icon.md', color: 'icon.default' },
  },
  variants: {
    active: {
      true: {
        bg: 'bg.selected',
        // Label and icon both take the brand colour, as in the reference — the
        // fill alone is too quiet to carry selection.
        color: 'brand.fg',
        fontWeight: 'semibold',
        _hover: { bg: 'bg.selected', color: 'brand.fg' },
        '& svg': { color: 'brand.fg' },
        // Thin amber indicator: selection without shouting.
        _before: {
          content: '""',
          position: 'absolute',
          insetInlineStart: '0',
          insetBlock: '1.5',
          width: '2px',
          borderRadius: 'full',
          bg: 'brand.solid',
        },
      },
      false: {},
    },
    collapsed: {
      true: { justifyContent: 'center', paddingInline: '0' },
      false: {},
    },
  },
  defaultVariants: { active: false, collapsed: false },
})

export const recipes = { button, input, textarea, statusBadge, navItem }
